import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { SankeyController, Flow } from "chartjs-chart-sankey";
import { Chart } from "react-chartjs-2";
import api from "../utils/api";
import ChartBurgerMenu from "./ChartBurgerMenu";

ChartJS.register(
  SankeyController,
  Flow,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
);

const COLOR_PALETTE = [
  "#009f8d", // Teal Main
  "#ff9c07", // Orange Main
  "#1e88e5", // Blue
  "#8e24aa", // Purple
  "#0ea5e9", // Light Blue
  "#facc15", // Yellow
  "#00c6a9", // Light Teal
  "#ffb74d", // Light Orange
  "#6d28d9", // Deep Purple
  "#10b981", // Green
  "#f43f5e", // Rose Red
  "#059669", // Dark Green
];

const getNodeColor = (depth) =>
  COLOR_PALETTE[(depth - 1) % COLOR_PALETTE.length];

const parseEnergy = (val) => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const num = parseFloat(val.replace(/[^\d.-]/g, ""));
    return isNaN(num) ? 0 : num;
  }
  return 0;
};

const SankeyDiagram = ({ username, dateRange, onLoadingChange }) => {
  const [hierarchyData, setHierarchyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [maxLevels, setMaxLevels] = useState(2);
  const chartRef = useRef(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
      setIsFullScreen(isFull);
      if (isFull) {
        setMaxLevels(10);
      } else {
        setMaxLevels(2);
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    const fetchData = async () => {
      if (!username || !dateRange?.start || !dateRange?.end) return;
      setLoading(true);
      setError(null);
      try {
        const formatDateTime = (d) => {
          if (!d) return "";
          const dateObj = typeof d === "string" ? new Date(d) : d;
          if (isNaN(dateObj.getTime())) return d;

          const isoString = new Date(
            dateObj.getTime() - dateObj.getTimezoneOffset() * 60000,
          ).toISOString();
          return isoString.replace("T", " ").slice(0, 19);
        };
        const res = await api.get("/energy-hierarchy-tree", {
          params: {
            username,
            start: formatDateTime(dateRange.start),
            end: formatDateTime(dateRange.end),
          },
        });
        setHierarchyData(res.data);
      } catch (e) {
        console.error(e);
        setError("Failed to load energy flow data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [username, dateRange]);

  const { chartData, nodeStyles } = useMemo(() => {
    if (!hierarchyData) return { chartData: { datasets: [] }, nodeStyles: {} };

    const rawPoints = [];
    const nodeStyles = {};
    const processedNodes = new Set();

    const traverseNode = (node, depth = 1) => {
      if (depth > maxLevels) return;

      const nodeId = node.machine_name;

      if (processedNodes.has(nodeId)) return;
      processedNodes.add(nodeId);

      const nodeEnergy = parseEnergy(node.energy);
      if (nodeEnergy <= 0) return;

      nodeStyles[nodeId] = {
        label: `${node.machine_name} (${nodeEnergy.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kWh)`,
        color: node.color || getNodeColor(depth),
      };

      (node.children || []).forEach((child) => {
        const childId = child.machine_name;
        const childEnergy = parseEnergy(child.energy);
        if (childEnergy <= 0) return;

        rawPoints.push({ from: nodeId, to: childId, flow: childEnergy });
        traverseNode(child, depth + 1);
      });
    };

    const nodes = Object.values(hierarchyData);
    const minPathLength = Math.min(
      ...nodes.map(
        (n) => (n.hierarchy_path || "").split("/").filter(Boolean).length,
      ),
    );
    const rootNodes = nodes.filter(
      (n) =>
        (n.hierarchy_path || "").split("/").filter(Boolean).length ===
        minPathLength,
    );

    rootNodes.forEach((root) => traverseNode(root));

    const maxFlow = rawPoints.reduce((m, p) => Math.max(m, p.flow), 0);
    const minVisible = maxFlow * 0.2;
    const dataPoints = rawPoints.map((p) => ({
      from: p.from,
      to: p.to,
      flow: Math.max(p.flow, minVisible),
      realFlow: p.flow,
    }));

    return {
      chartData: {
        datasets: [
          {
            label: "Energy Flow",
            data: dataPoints,
            colorFrom: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            colorTo: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            colorMode: "from",
            alpha: 0.6,
            hoverColorFrom: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            hoverColorTo: (c) => nodeStyles[c?.raw?.from]?.color || "#94a3b8",
            hoverColorMode: "from",
            hoverAlpha: 1,
            size: "max",
            borderWidth: 0,
            labels: {
              formatter: (v) => nodeStyles[v]?.label || v,
              font: { size: isFullScreen ? 12 : 10, weight: "bold" },
              color: "#334155",
            },
            node: { width: isFullScreen ? 35 : 25, padding: isFullScreen ? 30 : 20 },
          },
        ],
      },
      nodeStyles,
    };
  }, [hierarchyData, isFullScreen, maxLevels]);

  if (loading)
    return <div style={{ textAlign: "center", padding: 20 }}>Loading…</div>;
  if (error)
    return (
      <div style={{ textAlign: "center", padding: 20, color: "red" }}>
        {error}
      </div>
    );
  if (!hierarchyData) return null;

  const flowRows = chartData?.datasets?.[0]?.data || [];

  return (
    <div
      ref={chartRef}
      style={{
        background: "#fff",
        borderRadius: isFullScreen ? 0 : 16,
        padding: isFullScreen ? 40 : 20,
        minHeight: isFullScreen ? "100vh" : 500,
        width: "100%",
        height: isFullScreen ? "100vh" : "auto",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <h3 style={{ margin: 0 }}>Energy Flow Diagram</h3>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#f8fafc", padding: "4px 12px", borderRadius: "20px", border: "1px solid #e2e8f0" }}>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>Levels:</span>
            <select
              value={maxLevels}
              onChange={(e) => setMaxLevels(Number(e.target.value))}
              style={{ border: "none", background: "transparent", fontSize: "12px", fontWeight: 700, color: "#0f172a", cursor: "pointer", outline: "none" }}
            >
              {[2, 3, 4, 5, 6, 10].map((l) => (
                <option key={l} value={l}>{l === 10 ? "Full" : l}</option>
              ))}
            </select>
          </div>
        </div>
        <ChartBurgerMenu
          chartRef={chartRef}
          chartTitle="Energy_Flow_Sankey"
          exportContainer={true}
          chartData={chartData.datasets[0].data.map((d) => [
            d.from,
            d.to,
            d.flow,
          ])}
          chartType="sankey"
        />
      </div>
      {flowRows.length > 0 ? (
        <div
          key={isFullScreen ? "full" : "dash"}
          style={{ flex: 1, minHeight: isFullScreen ? "80vh" : 250, width: "100%" }}
        >
          <Chart
            type="sankey"
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      if (ctx.raw?.flow !== undefined) {
                        const f = nodeStyles[ctx.raw.from]?.label || ctx.raw.from;
                        const t = nodeStyles[ctx.raw.to]?.label || ctx.raw.to;
                        const realKwh = ctx.raw.realFlow ?? ctx.raw.flow;
                        return `${f} \u2192 ${t}: ${realKwh.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kWh`;
                      }
                      return ctx.label || "";
                    },
                  },
                },
              },
            }}
          />
        </div>
      ) : (
        <div
          style={{
            height: 450,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "#64748b",
          }}
        >
          This hierarchy does not have enough layers to build a flow diagram.
        </div>
      )}
    </div>
  );
};

export default SankeyDiagram;
